import React from 'react';
import useCheckoutState, { checkoutLayouts } from '../hooks/useCheckoutState';
import clsx from 'clsx';
import { checkHasEventModules } from '../enums/modules';
import TicketGroup from './TicketGroup';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { formatPrice } from '../services/formatPrice';
import Label from './CheckoutForm/components/Label';
import checkIsTicketGroupVisible from '../lib/checkIsTicketGroupVisible';
import { getImageSrcAndSrcSet } from '../services/getUrlFromCdn';

export default function TicketGroups() {
  const { checkout, checkoutLayout, isPreview, seller, form, query } = useCheckoutState();

  const { ticketGroups: formTicketGroups } = form;

  const eventDetails = checkout?.eventDetails || {};

  // only show enabled ticket groups
  // only show ticket groups with ticket types
  const ticketGroups = (checkout?.ticketGroups || [])
    .filter(ticketGroup => ticketGroup.status === 'enabled')
    .filter(ticketGroup => ticketGroup.ticketTypes.length > 0);

  if (!ticketGroups.length) {
    return null;
  }

  if (ticketGroups.length === 1 && !ticketGroups[0].layout?.collapse) {
    const ticketGroup = ticketGroups[0];

    const visible = checkIsTicketGroupVisible(ticketGroup, query);

    if (!visible && !isPreview) return null;

    return (
      <div className="cp_ticket-groups">
        <div
          className={clsx('border-b border-gray-100 pb-4 px-4', {
            'opacity-25 pointer-events-none': !visible && isPreview,
          })}
        >
          <TicketGroup ticketGroup={ticketGroup} currency={eventDetails.currency} />
        </div>
      </div>
    );
  }

  const hasEventModules = checkHasEventModules({ checkout, seller });

  return (
    <div
      className={clsx('cp_ticket-groups', {
        'mx-4':
          checkoutLayout === checkoutLayouts.SPLIT || checkoutLayout === checkoutLayouts.DOUBLE,
      })}
    >
      {ticketGroups.map(ticketGroup => {
        const visible = checkIsTicketGroupVisible(ticketGroup, query);

        if (!visible && !isPreview) {
          return null;
        }

        // not accordion
        if (!ticketGroup.layout?.collapse) {
          return (
            <div
              key={ticketGroup._id}
              className={clsx('py-2 last:border-b border-gray-100 last:pb-4 pt-4', {
                'opacity-25 pointer-events-none': !visible && isPreview,
                'first:pt-0': checkoutLayout === checkoutLayouts.DOUBLE,
                'px-4': checkoutLayout === checkoutLayouts.CENTER,
                'first:border-t':
                  hasEventModules &&
                  (checkoutLayout === checkoutLayouts.CENTER ||
                    checkoutLayout === checkoutLayouts.SPLIT),
              })}
            >
              <TicketGroup ticketGroup={ticketGroup} />
            </div>
          );
        }

        const selectedTicketTypes = ticketGroup.ticketTypes
          .filter(ticketType => {
            const formTicketType = formTicketGroups?.[ticketGroup._id]?.find(
              formTicketGroup => formTicketGroup.ticketTypeId === ticketType._id,
            );

            if (!formTicketType) return false;

            return formTicketType.quantity > 0;
          })
          .map(ticketType => {
            const formTicketType = formTicketGroups?.[ticketGroup._id]?.find(
              formTicketGroup => formTicketGroup.ticketTypeId === ticketType._id,
            );

            if (!formTicketType) return false;

            return {
              ...ticketType,
              quantity: formTicketType.quantity,
            };
          });

        // accordion
        return (
          <Disclosure
            key={ticketGroup._id}
            as="div"
            className={clsx('first:border-t border-b border-gray-100', {
              'opacity-25 pointer-events-none': !visible && isPreview,
              'px-4': checkoutLayout === checkoutLayouts.CENTER,
            })}
          >
            {({ open }) => (
              <>
                <DisclosureButton className={clsx('py-2 my-2 w-full')}>
                  <div className="flex justify-between items-center">
                    <Label
                      style={{ marginBottom: 0, cursor: 'inherit' }}
                      required={ticketGroup.required}
                    >
                      {ticketGroup.name}
                    </Label>
                    <ChevronDownIcon className="h-5 w-5" />
                  </div>

                  {/* selected ticket types in closed accordion */}
                  {selectedTicketTypes &&
                    selectedTicketTypes.length > 0 &&
                    !open &&
                    selectedTicketTypes.map(selectedTicketType => (
                      <div
                        className={
                          'text-left mt-2 text-gray-900 flex border-b last:border-b-0 border-gray-200'
                        }
                        key={selectedTicketType.ticketTypeId}
                      >
                        {ticketGroup.ticketSelectionType === 'quantity' && (
                          <div className="mr-1 whitespace-nowrap">
                            {selectedTicketType.quantity}
                            <XMarkIcon className="w-3 h-3 mt-0.5 inline-block" />
                          </div>
                        )}

                        {ticketGroup.ticketSelectionType !== 'quantity' && (
                          <CheckIcon className="w-5 h-5 mr-1 mt-0.5" />
                        )}

                        <div>
                          <SelectedAccordionTicketTypeLabel
                            ticketType={selectedTicketType}
                            showName={ticketGroup.layout?.showTicketTypeName}
                            showPrice={ticketGroup.layout?.showTicketTypePrices}
                            alignment={ticketGroup.layout?.textAlign}
                            imageSize={ticketGroup.layout?.imageSize}
                            soldOutText={checkout.soldOutText}
                          />
                        </div>
                      </div>
                    ))}
                </DisclosureButton>

                <DisclosurePanel className={clsx('pb-4')}>
                  <TicketGroup ticketGroup={ticketGroup} isAccordion />
                </DisclosurePanel>
              </>
            )}
          </Disclosure>
        );
      })}
    </div>
  );
}

function SelectedAccordionTicketTypeLabel({ ticketType, showName, showPrice, alignment = 'left' }) {
  const { product, checkout, isPreview } = useCheckoutState();

  const price = formatPrice({
    amount: ticketType.price,
    currency: product.currency,
    locale: checkout.locale,
    style: 'currency',
  });

  const image = getImageSrcAndSrcSet({
    url: ticketType.image?.url,
    width: 48,
    height: 48,
    isPreview,
  });

  return (
    <div className="flex flex-wrap w-full gap-2">
      <div
        className={clsx('flex flex-nowrap w-full gap-x-2', {
          'text-left': alignment === 'left',
        })}
      >
        {image && (
          <div
            className={clsx(
              'cp_ticket-type-image-wrapper w-12 h-12 rounded overflow-hidden flex-shrink-0',
            )}
          >
            <img
              src={image.src}
              srcSet={image.srcSet}
              className="cp_ticket-type-image object-cover h-full w-full"
            />
          </div>
        )}

        <div className="cp_ticket-type-text-wrapper gap-x-1.5 gap-y-0.5 flex flex-col">
          {showName && (
            <div className="cp_ticket-type-name overflow-hidden flex-shrink-0 font-medium">
              {ticketType.name}
            </div>
          )}
        </div>

        <div className="ml-auto">
          {showPrice && (
            <div
              className={clsx('cp_ticket-type-price-wrapper gap-x-1.5 gap-y-0.5 flex flex-col', {
                'text-right': showName && alignment === 'left',
                'flex-1': !showName,
              })}
            >
              <div
                className={clsx(
                  'cp_ticket-type-price whitespace-nowrap space-y-1 font-medium w-full',
                  {
                    'text-right': alignment === 'left',
                  },
                )}
              >
                {price}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
